import { type FC } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useGetWorkflow, useGetWorkflowExecution, useListWorkflowExecutionActions } from "@/queries/automation-queries";
import { workflowDetailsUrl, workflowName, workflowsUrl } from "@/utils/automations";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import { WorkflowExecutionSidebar } from "@/components/automations/AutomationExecutionSidebar";
import { WorkflowExecutionActionsDropdown } from "@/components/automations/AutomationExecutionActionsDropdown";
import { AutomationExecutionPageSelector } from "@/components/automations/AutomationExecutionPageSelector";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { SubPageHeader } from "@/components/flexkit/page/SubPageHeader";
import { Page } from "@/components/flexkit/page/Page";
import { PageContent } from "@/components/flexkit/page/PageContent";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";

export const AutomationExecutionLayout: FC = () => {
    const { workflowExecutionId } = useParams<{ workflowExecutionId: string }>();
    const { data: workflowExecution } = useGetWorkflowExecution(workflowExecutionId);
    const { data: workflowExecutionActions } = useListWorkflowExecutionActions({
        enabled: !!workflowExecutionId,
        workflowExecutionId: workflowExecutionId || "",
    });
    const { data: workflow } = useGetWorkflow(workflowExecution?.metadata?.workflowId);
    const { isMobileViewport } = useIsMobileViewport();

    useDocumentTitle(workflow ? `${workflowName(workflow)} - Execution` : undefined);

    const headerActions = workflowExecution ? (
        <WorkflowExecutionActionsDropdown execution={workflowExecution} />
    ) : undefined;

    return (
        <Page data-testid="workflow-execution-layout">
            <SubPageHeader
                breadcrumbs={
                    workflow
                        ? [
                              { label: "Automations", to: workflowsUrl() },
                              { label: workflowName(workflow), to: workflowDetailsUrl(workflow) },
                              { label: "Execution report" },
                          ]
                        : undefined
                }
                actions={headerActions}
                loading={workflowExecution && workflow ? undefined : { breadcrumbItemCount: 3 }}
            />

            {isMobileViewport ? (
                <PageContent>
                    <div className="flex flex-col gap-4">
                        <AutomationExecutionPageSelector
                            workflowExecutionId={workflowExecutionId || ""}
                            actions={workflowExecutionActions || []}
                        />
                        <Outlet />
                    </div>
                </PageContent>
            ) : (
                <PageContent padding="none" scrollable={false}>
                    <ResizablePanelGroup
                        direction="horizontal"
                        className="flex h-full"
                        autoSaveId="workflow-execution-layout"
                    >
                        <ResizablePanel defaultSize={25} minSize={20} maxSize={40} order={1}>
                            <ScrollArea orientation="vertical" className="h-full">
                                <div className="py-6 pl-6 pr-[21px]">
                                    <WorkflowExecutionSidebar
                                        workflowExecutionId={workflowExecutionId || ""}
                                        actions={workflowExecutionActions || []}
                                    />
                                </div>
                            </ScrollArea>
                        </ResizablePanel>
                        <ResizableHandle withHandle={false} />
                        <ResizablePanel defaultSize={75} minSize={60} order={2}>
                            <ScrollArea orientation="vertical" className="h-full">
                                <div className="py-6 pl-6 pr-[21px]">
                                    <Outlet />
                                </div>
                            </ScrollArea>
                        </ResizablePanel>
                    </ResizablePanelGroup>
                </PageContent>
            )}
        </Page>
    );
};
